/**
 * Confirmación explícita de una acción sensible.
 *
 * Qué cuenta como sensible acá: un cambio de rol, un alta o una baja de
 * usuario, y el borrado de la cuenta de un consumidor. Las tres cambian quién
 * puede hacer qué o destruyen datos que no vuelven, y las tres se disparan
 * desde un botón que está a un click de distancia de otro que no hace nada
 * de eso.
 *
 * Dos niveles, deliberados:
 *
 *   · **Confirmar** — un diálogo modal que dice qué va a pasar. Alcanza para
 *     lo reversible.
 *   · **Escribir la palabra** (`confirmWord`) — para lo irreversible. Un
 *     "¿estás seguro?" con un botón se contesta que sí por reflejo; tipear
 *     BORRAR no.
 *
 * Accesibilidad, que en un modal no es decoración: `role="dialog"` +
 * `aria-modal`, el foco entra al diálogo al abrirse y **vuelve al botón que
 * lo abrió** al cerrarse, Escape cancela, y el título y el detalle están
 * enlazados por `aria-labelledby` / `aria-describedby` para que un lector de
 * pantalla anuncie qué se está por hacer antes que los botones.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  detail: string;
  /** Texto del botón que confirma. Por defecto, `confirm.accept`. */
  confirmLabel?: string;
  /** Si viene, hay que tipearla exactamente para habilitar el botón. */
  confirmWord?: string;
  /** `true` mientras la mutación está en vuelo. */
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * El diálogo se **monta y se desmonta**; no se esconde con CSS. Así el texto
 * tipeado en `confirmWord` no sobrevive a un cierre —cancelar y volver a
 * abrir empieza de cero, sin ningún `setState` dentro de un efecto— y el
 * modal no queda en el árbol robándole el foco a la pantalla de atrás.
 */
export function ConfirmDialog({ open, ...props }: ConfirmDialogProps): ReactNode {
  if (!open) return null;
  return <ConfirmPanel {...props} />;
}

function ConfirmPanel({
  title,
  detail,
  confirmLabel,
  confirmWord,
  busy = false,
  onCancel,
  onConfirm,
}: Omit<ConfirmDialogProps, 'open'>): ReactNode {
  const { t } = useI18n();
  const titleId = useId();
  const detailId = useId();
  const [typed, setTyped] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  // Foco y teclado. Es manejo de DOM, no de datos: acá `useEffect` es lo que
  // corresponde — la regla del andamio prohíbe `useEffect` para **pedir
  // datos**, que es otra cosa.
  useEffect(() => {
    openerRef.current = document.activeElement;
    // El panel tiene `tabIndex={-1}`: recibe el foco sin entrar en el orden
    // de tabulación, así el lector de pantalla arranca por el título.
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const opener = openerRef.current;
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [onCancel]);

  const needsWord = confirmWord !== undefined;
  const canConfirm = !busy && (!needsWord || typed.trim() === confirmWord);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={detailId}
        tabIndex={-1}
        className="w-full max-w-md rounded-(--radius-card) border border-border bg-surface-raised p-5 shadow-lg"
      >
        <h2 id={titleId} className="text-base font-semibold text-ink">
          {title}
        </h2>
        <p id={detailId} className="mt-2 text-sm text-ink-muted">
          {detail}
        </p>

        {needsWord && (
          <label className="mt-4 block text-sm">
            <span className="text-ink-muted">
              {t('confirm.typeToConfirm', { word: confirmWord })}
            </span>
            <input
              value={typed}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
              autoComplete="off"
              className="mt-1 w-full rounded border border-border-strong bg-surface px-3 py-2 font-mono text-sm text-ink"
            />
          </label>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('confirm.cancel')}
          </Button>
          <Button variant="critical" onClick={onConfirm} disabled={!canConfirm}>
            {confirmLabel ?? t('confirm.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}
